import React, { useState, useEffect, useRef } from "react";
import { NavLink, useLocation, useNavigate, Link } from "react-router-dom";

import {
  FaHome,
  FaBicycle,
  FaGamepad,
  FaInfoCircle,
  FaShoppingCart,
  FaBars,
  FaTimes,
  FaUser,
  FaEdit,
  FaBoxOpen,
  FaSignOutAlt,
  FaChevronDown,
} from "react-icons/fa";

import { useCart } from "../components/CartContext";
import { useAuth } from "../components/AuthContext";

import "../styles/Navbar.css";

/* ================= SCROLL PROGRESS ================= */

const ScrollProgress = () => {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;

      const docHeight =
        document.documentElement.scrollHeight -
        window.innerHeight;

      const scrolled =
        docHeight > 0
          ? (scrollTop / docHeight) * 100
          : 0;

      setWidth(scrolled);
    };

    window.addEventListener(
      "scroll",
      handleScroll,
      {
        passive: true,
      }
    );

    return () =>
      window.removeEventListener(
        "scroll",
        handleScroll
      );
  }, []);

  return (
    <div className="scroll-progress-container">
      <div
        className="scroll-progress-main"
        style={{
          width: `${width}%`,
        }}
      />
    </div>
  );
};

/* ================= NAVBAR ================= */

const Navbar = () => {
  const { pathname } = useLocation();

  const navigate = useNavigate();

  const { cart = [] } = useCart();

  const { user, logout } = useAuth();

  /* ================= REFS ================= */

  const mobileProfileRef = useRef(null);

  const desktopProfileRef = useRef(null);

  const navRef = useRef(null);

  /* ================= STATES ================= */

  const [isMenuOpen, setIsMenuOpen] =
    useState(false);

  const [showProfileMenu, setShowProfileMenu] =
    useState(false);

  const [
    showMobileProfileMenu,
    setShowMobileProfileMenu,
  ] = useState(false);

  /* DESKTOP DROPDOWN */
  const [openDropdown, setOpenDropdown] =
    useState(null);

  /* MOBILE DROPDOWN */
  const [mobileDropdown, setMobileDropdown] =
    useState(null);

  /* ================= CART ================= */

  const cartCount = (cart || []).reduce(
    (sum, item) =>
      sum + (item.quantity || 0),
    0
  );

  /* ================= USER AVATAR ================= */

  const userAvatar =
    user?.avatar ||
    `https://ui-avatars.com/api/?name=${
      user?.name || "User"
    }&background=0D8ABC&color=fff`;

  /* ================= EFFECTS ================= */

  /* CLOSE MENUS ON ROUTE CHANGE */

  useEffect(() => {
    setIsMenuOpen(false);

    setShowProfileMenu(false);

    setShowMobileProfileMenu(false);

    setOpenDropdown(null);

    setMobileDropdown(null);
  }, [pathname]);

  /* CLOSE MOBILE MENU ON DESKTOP */

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsMenuOpen(false);

        setMobileDropdown(null);
      }
    };

    window.addEventListener(
      "resize",
      handleResize
    );

    return () =>
      window.removeEventListener(
        "resize",
        handleResize
      );
  }, []);

  /* OUTSIDE CLICK + ESC */

  useEffect(() => {
    const handleClickOutside = (e) => {
      /* MOBILE PROFILE */

      if (
        mobileProfileRef.current &&
        e.target instanceof Node &&
        !mobileProfileRef.current.contains(
          e.target
        )
      ) {
        setShowMobileProfileMenu(false);
      }

      /* DESKTOP PROFILE */

      if (
        desktopProfileRef.current &&
        e.target instanceof Node &&
        !desktopProfileRef.current.contains(
          e.target
        )
      ) {
        setShowProfileMenu(false);
      }

      /* DESKTOP NAV */

      if (
        navRef.current &&
        e.target instanceof Node &&
        !navRef.current.contains(e.target)
      ) {
        setOpenDropdown(null);
      }
    };

    const handleEsc = (e) => {
      if (e.key === "Escape") {
        setIsMenuOpen(false);

        setShowProfileMenu(false);

        setShowMobileProfileMenu(false);

        setOpenDropdown(null);

        setMobileDropdown(null);
      }
    };

    document.addEventListener(
      "mousedown",
      handleClickOutside
    );

    document.addEventListener(
      "keydown",
      handleEsc
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );

      document.removeEventListener(
        "keydown",
        handleEsc
      );
    };
  }, []);

  /* ================= LOGOUT ================= */

  const handleLogout = () => {
    logout();

    navigate("/");
  };

  /* ================= MENU ITEMS ================= */

  const menuItems = [
    {
      key: "home",
      label: "Home",
      icon: <FaHome />,
      path: "/",
    },

    {
      key: "toys",
      label: "Toys",
      icon: <FaBicycle />,
      path: "/toys",

      // submenu: [
      //   {
      //     label: "All Toys",
      //   },

      //   {
      //     label: "Bikes",
      //     path: "/toys/bikes",
      //   },

      //   {
      //     label: "Jeeps",
      //     path: "/toys/jeeps",
      //   },

      //   {
      //     label: "Cars",
      //     path: "/toys/cars",
      //   },
      // ],
    },

    {
      key: "bicycles",
      label: "Bicycles",
      icon: <FaBicycle />,
      path: "/products",
    },

    {
      key: "about",
      label: "About",
      icon: <FaInfoCircle />,
      path: "/about",
    },

    {
      key: "cart",
      label: "Cart",
      icon: <FaShoppingCart />,
      path: "/cart",
      badge: cartCount,
    },
    
...(user
  ? [
      {
        key: "game-zone",
        label: "Game Zone",
        icon: <FaGamepad />,
        path: "/coming-soon",
      },
    ]
  : []),
  ];

  return (
    <header className="navbar">
      <ScrollProgress />

      {/* ================= MOBILE HEADER ================= */}

      <div className="mobile-header">
        <Link
          to="/"
          className="mobile-logo"
        >
          <img
            src="https://i.ibb.co/MwHMzcZ/Whats-App-Image-2025-08-05-at-10-30-04-AM-removebg-preview.png"
            alt="Logo"
          />
        </Link>

        <div className="mobile-actions">

          {/* MOBILE PROFILE */}

          {user && (
            <div
              className="mobile-profile"
              ref={mobileProfileRef}
            >
              <img
                src={userAvatar}
                alt="User"
                onClick={() =>
                  setShowMobileProfileMenu(
                    !showMobileProfileMenu
                  )
                }
              />

              {showMobileProfileMenu && (
                <div className="profile-dropdown mobile">
                  <Link to="/profile">
                    <FaEdit /> Profile
                  </Link>

                  <Link to="/my-orders">
                    <FaBoxOpen /> Orders
                  </Link>

                  <button
                    type="button"
                    className="logout-btn"
                    onClick={handleLogout}
                  >
                    <FaSignOutAlt /> Logout
                  </button>
                </div>
              )}
            </div>
          )}

          {/* HAMBURGER */}

          <button
            type="button"
            className="hamburger"
            onClick={() =>
              setIsMenuOpen(!isMenuOpen)
            }
          >
            {isMenuOpen ? (
              <FaTimes />
            ) : (
              <FaBars />
            )}
          </button>
        </div>
      </div>

      {/* ================= DESKTOP NAVBAR ================= */}

      <div
        className="navbar-inner"
        ref={navRef}
      >
        {/* LOGO */}

        <Link
          to="/"
          className="navbar-logo"
        >
          <img
            src="https://i.ibb.co/MwHMzcZ/Whats-App-Image-2025-08-05-at-10-30-04-AM-removebg-preview.png"
            alt="Logo"
          />
        </Link>

        {/* ================= DESKTOP NAV ================= */}

        <nav className="desktop-nav">
          <ul>
            {menuItems.map((item) => (
              <li
                key={item.key}
                className="nav-item"
              >
                {item.submenu ? (
                  <div className="dropdown-wrapper">
                    <button
                      type="button"
                      className="menu-parent"
                      onClick={() =>
                        setOpenDropdown(
                          openDropdown ===
                            item.key
                            ? null
                            : item.key
                        )
                      }
                    >
                      {item.icon}

                      <span>
                        {item.label}
                      </span>

                      <FaChevronDown />
                    </button>

                    <ul
                      className={`submenu ${
                        openDropdown ===
                        item.key
                          ? "open"
                          : ""
                      }`}
                    >
                      {item.submenu.map(
                        (sub, i) => (
                          <li key={i}>
                            <NavLink
                              to={sub.path}
                            >
                              {sub.label}
                            </NavLink>
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                ) : (
                  <NavLink to={item.path}>
                    {item.icon}

                    <span>
                      {item.label}
                    </span>

                    {item.badge &&
                      item.badge > 0 && (
                        <span className="cart-badge">
                          {item.badge}
                        </span>
                      )}
                  </NavLink>
                )}
              </li>
            ))}
          </ul>
        </nav>

        {/* ================= DESKTOP PROFILE ================= */}

        <div
          className="navbar-actions"
          ref={desktopProfileRef}
        >
          {user ? (
            <div className="profile-container">
              <button
                type="button"
                className="profile-wrapper"
                onClick={() =>
                  setShowProfileMenu(
                    !showProfileMenu
                  )
                }
              >
                <img
                  src={userAvatar}
                  alt="User"
                />

                <span className="profile-name">
                  {user?.name || "User"}
                </span>
              </button>

              {showProfileMenu && (
                <div className="profile-dropdown">
                  <Link to="/profile">
                    <FaEdit /> Profile
                  </Link>

                  <Link to="/my-orders">
                    <FaBoxOpen /> Orders
                  </Link>

                  <button
                    type="button"
                    className="logout-btn"
                    onClick={handleLogout}
                  >
                    <FaSignOutAlt /> Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link
              to="/login"
              className="login-link"
            >
              <FaUser /> Login
            </Link>
          )}
        </div>
      </div>

      {/* ================= MOBILE MENU ================= */}

      <nav
        className={`mobile-nav-menu ${
          isMenuOpen ? "active" : ""
        }`}
      >
        <ul>
          {menuItems.map((item) => (
            <li key={item.key}>
              {item.submenu ? (
                <>
                  <button
                    type="button"
                    className={`mobile-menu-parent ${
                      mobileDropdown ===
                      item.key
                        ? "active"
                        : ""
                    }`}
                    onClick={() =>
                      setMobileDropdown(
                        mobileDropdown ===
                          item.key
                          ? null
                          : item.key
                      )
                    }
                  >
                    <div className="mobile-menu-left">
                      {item.icon}

                      <span>
                        {item.label}
                      </span>
                    </div>

                    <FaChevronDown
                      className={`mobile-dropdown-icon ${
                        mobileDropdown ===
                        item.key
                          ? "rotate"
                          : ""
                      }`}
                    />
                  </button>

                  <ul
                    className={`mobile-submenu ${
                      mobileDropdown ===
                      item.key
                        ? "open"
                        : ""
                    }`}
                  >
                    {item.submenu.map(
                      (sub, i) => (
                        <li key={i}>
                          <NavLink
                            to={sub.path}
                          >
                            {sub.label}
                          </NavLink>
                        </li>
                      )
                    )}
                  </ul>
                </>
              ) : (
                <NavLink to={item.path}>
                  {item.icon}

                  <span>{item.label}</span>
                </NavLink>
              )}
            </li>
          ))}

          {!user && (
            <li>
              <Link
                to="/login"
                className="mobile-login-btn"
              >
                Login
              </Link>
            </li>
          )}
        </ul>
      </nav>
    </header>
  );
};

export default Navbar;